import * as React from "react";
import { Badge, Button, ScrollArea, useToast } from "@repo/ui";
import { Info, Loader2, RotateCcw, Save, Undo2 } from "lucide-react";
import { PermissionCell } from "@/components/settings/permission-cell";
import { clearOverridesCache } from "@/hooks/use-permission";
import { useApproval } from "@/context/approval-context";
import { handlePermissionError } from "@/lib/utils/handle-permission-error";
import {
  getPermissionOverrides,
  updatePermissionOverrides,
} from "@/lib/api/permissions";
import { ApiClientError } from "@/lib/api/client";
import {
  PERMISSION_CATEGORIES,
  PERMISSION_LABELS,
  PERMISSION_MATRIX,
  ROLE_DISPLAY_NAMES,
  resolveEffectivePermission,
} from "@/lib/permissions";
import type {
  Permission,
  PermissionAccess,
  PermissionOverride,
  Role,
} from "@/lib/permissions";

/** Permissions that only support allowed/denied (no approval option). */
const BINARY_PERMISSIONS = new Set([
  "inventory:add_product",
  "users:create",
  "users:deactivate",
  "settings:general",
  "settings:pharmacy",
  "settings:subscription",
  "reminders:create",
  "reminders:edit",
  "reminders:delete",
  "reminders:complete",
]);

function isBinaryPermission(permission: string): boolean {
  return permission.endsWith(":view") || BINARY_PERMISSIONS.has(permission);
}

interface PermissionsEditorProps {
  role: Role;
  /** Optional description text shown above the editor. */
  description?: React.ReactNode;
}

export function PermissionsEditor({
  role,
  description,
}: PermissionsEditorProps) {
  const { toast } = useToast();
  const { requestApproval } = useApproval();
  const [savedOverrides, setSavedOverrides] = React.useState<
    PermissionOverride[]
  >([]);
  const [pendingOverrides, setPendingOverrides] = React.useState<
    PermissionOverride[]
  >([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [activeCategoryId, setActiveCategoryId] = React.useState<string>("");

  // Filter categories to only those with permissions for this role
  const visibleCategories = React.useMemo(() => {
    return PERMISSION_CATEGORIES.filter(
      (cat) =>
        cat.permissions.filter(
          (perm) => PERMISSION_MATRIX[role]?.[perm] !== undefined
        ).length > 0
    );
  }, [role]);

  const hasChanges = React.useMemo(() => {
    if (savedOverrides.length !== pendingOverrides.length) return true;
    return pendingOverrides.some(
      (po) =>
        !savedOverrides.find(
          (so) =>
            so.role === po.role &&
            so.permission === po.permission &&
            so.access === po.access
        )
    );
  }, [savedOverrides, pendingOverrides]);

  const fetchOverrides = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getPermissionOverrides();
      const overrides = data.overrides as PermissionOverride[];
      setSavedOverrides(overrides);
      setPendingOverrides(overrides);
    } catch {
      toast({
        title: "Error",
        description: "Failed to load permission overrides.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // Fetch overrides when role changes
  React.useEffect(() => {
    fetchOverrides();
  }, [role, fetchOverrides]);

  // Set initial active category
  React.useEffect(() => {
    if (visibleCategories.length > 0) {
      setActiveCategoryId((prev) => {
        // Keep current selection if still valid
        if (prev && visibleCategories.some((c) => c.id === prev)) return prev;
        return visibleCategories[0]!.id;
      });
    }
  }, [visibleCategories]);

  function handleAccessChange(
    permission: Permission,
    newAccess: PermissionAccess
  ) {
    const { defaultAccess } = resolveEffectivePermission(
      role,
      permission,
      pendingOverrides
    );

    setPendingOverrides((prev) => {
      const filtered = prev.filter(
        (o) => !(o.role === role && o.permission === permission)
      );
      if (newAccess === defaultAccess) {
        return filtered;
      }
      return [...filtered, { role, permission, access: newAccess }];
    });
  }

  function handleResetCategory(categoryId: string) {
    const category = PERMISSION_CATEGORIES.find((c) => c.id === categoryId);
    if (!category) return;

    setPendingOverrides((prev) =>
      prev.filter(
        (o) =>
          !(
            o.role === role &&
            category.permissions.includes(o.permission as Permission)
          )
      )
    );
  }

  function handleDiscard() {
    setPendingOverrides(savedOverrides);
  }

  async function handleSave(approvalToken?: string) {
    setIsSaving(true);
    try {
      await updatePermissionOverrides(
        { overrides: pendingOverrides },
        { approvalToken }
      );
      setSavedOverrides(pendingOverrides);
      clearOverridesCache();
      toast({
        title: "Permissions updated",
        description: `Permission overrides for ${ROLE_DISPLAY_NAMES[role]} have been saved.`,
        variant: "success",
      });
    } catch (error) {
      if (error instanceof ApiClientError) {
        if (
          handlePermissionError(error, {
            requestApproval,
            toast,
            retryFn: async (token) => handleSave(token),
            actionDescription: "Save permission overrides",
          })
        )
          return;
      }
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to save overrides.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  }

  const activeCategory = visibleCategories.find(
    (c) => c.id === activeCategoryId
  );
  const activePerms = activeCategory
    ? activeCategory.permissions.filter(
        (perm) => PERMISSION_MATRIX[role]?.[perm] !== undefined
      )
    : [];

  const activeCategoryOverrideCount = activePerms.filter((perm) => {
    const { isOverridden } = resolveEffectivePermission(
      role,
      perm,
      pendingOverrides
    );
    return isOverridden;
  }).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {description && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
          <Info className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">{description}</p>
        </div>
      )}

      {/* Two-panel layout */}
      <div className="flex min-h-[400px] rounded-lg border overflow-hidden">
        {/* Category sidebar */}
        <ScrollArea className="w-[200px] shrink-0 border-r bg-muted/20">
          <nav className="flex flex-col py-1">
            {visibleCategories.map((category) => {
              const isActive = category.id === activeCategoryId;
              const catPerms = category.permissions.filter(
                (perm) => PERMISSION_MATRIX[role]?.[perm] !== undefined
              );
              const overrideCount = catPerms.filter((perm) => {
                const { isOverridden } = resolveEffectivePermission(
                  role,
                  perm,
                  pendingOverrides
                );
                return isOverridden;
              }).length;

              return (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => setActiveCategoryId(category.id)}
                  className={`flex items-center justify-between gap-2 px-4 py-2.5 text-left text-sm transition-colors ${
                    isActive
                      ? "bg-background font-medium text-foreground border-r-2 border-primary"
                      : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                  }`}
                >
                  <span className="truncate">{category.label}</span>
                  {overrideCount > 0 && (
                    <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary/10 px-1.5 text-[10px] font-semibold text-primary">
                      {overrideCount}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </ScrollArea>

        {/* Permission detail panel */}
        <div className="flex-1 flex flex-col min-h-0">
          {activeCategory && (
            <>
              {/* Category header */}
              <div className="flex items-center justify-between px-5 py-3 border-b bg-muted/10">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold">
                    {activeCategory.label}
                  </h3>
                  {activeCategoryOverrideCount > 0 && (
                    <Badge
                      variant="outline"
                      className="text-[10px] border-primary/30 text-primary"
                    >
                      {activeCategoryOverrideCount} customized
                    </Badge>
                  )}
                </div>
                {activeCategoryOverrideCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1.5 text-xs text-muted-foreground"
                    onClick={() => handleResetCategory(activeCategory.id)}
                  >
                    <RotateCcw className="h-3 w-3" />
                    Reset to defaults
                  </Button>
                )}
              </div>

              {/* Permission rows */}
              <ScrollArea className="flex-1">
                <div className="divide-y">
                  {activePerms.map((perm) => {
                    const { access, isOverridden, defaultAccess } =
                      resolveEffectivePermission(role, perm, pendingOverrides);

                    return (
                      <div
                        key={perm}
                        className="flex items-center justify-between gap-4 px-5 py-3"
                      >
                        <div className="flex flex-col gap-0.5 min-w-0">
                          <span className="text-sm font-medium">
                            {PERMISSION_LABELS[perm] || perm}
                          </span>
                          {isOverridden && (
                            <span className="text-[11px] text-muted-foreground">
                              Default:{" "}
                              <span className="capitalize">
                                {defaultAccess === "requires_approval"
                                  ? "requires approval"
                                  : defaultAccess}
                              </span>
                            </span>
                          )}
                        </div>
                        <PermissionCell
                          access={access}
                          defaultAccess={defaultAccess}
                          isOverridden={isOverridden}
                          isReadOnly={isSaving}
                          viewOnly={isBinaryPermission(perm)}
                          onChange={(val) => handleAccessChange(perm, val)}
                        />
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </>
          )}
        </div>
      </div>

      {/* Footer actions */}
      <div className="flex items-center justify-between">
        {hasChanges ? (
          <Badge variant="outline" className="border-amber-300 text-amber-600">
            Unsaved changes
          </Badge>
        ) : (
          <div />
        )}
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleDiscard}
            disabled={!hasChanges || isSaving}
            className="gap-2"
          >
            <Undo2 className="h-3.5 w-3.5" />
            Discard
          </Button>
          <Button
            size="sm"
            onClick={() => handleSave()}
            disabled={!hasChanges || isSaving}
            className="gap-2"
          >
            {isSaving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5" />
            )}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}
